/**
 * ROOFTOPS family — PURE layout (no three.js, no DOM) for the realism pass on
 * `rooftops`, `rooftops_xl` and `rooftop_spiderweb` (2026-11-04, owner:
 * "improve each map", "realistic"). `rooftopsBuild.ts` turns the piece list
 * into merged meshes; `agent/bench-extras-rooftops.mjs` checks the list against
 * the REAL shared collider boxes.
 *
 * Inspiration (not copies): flat commercial roofs seen from a neighbouring
 * roof — single-ply membrane with darker repair patches, roof drains, concrete
 * walkway pavers, flush skylights and hatches, packaged rooftop HVAC units
 * with louvred sides and a top fan; on the facades galvanised downspouts with
 * conductor heads and scuppers, window AC units, meter boxes; small dishes and
 * TV aerials on the parapets; and on the skyscraper variant aviation masts
 * with red obstruction lights.
 *
 * WHY THIS SHAPE — these are maps about jumping between roofs, so the pass is
 * deliberately conservative about what it puts where (`roofFurniture()` in
 * mapDressing already opts these maps out of tall roof plant for that reason):
 *  - ROOF pieces are decals: ≤ ROOF_LIFT above the roof deck, inside the
 *    parapet, clear of every other collider and every jump pad. Nothing on a
 *    roof can be tripped on, hidden behind or mistaken for cover.
 *  - UNIT pieces dress the existing AC collider box: on its faces ≤ UNIT_PROUD
 *    proud, or ≤ UNIT_LID on its (already standable) top.
 *  - WALL pieces hang on a wall/parapet/slab face ≤ WALL_PROUD (0.26 m) proud,
 *    inside that face, never in any other collider. No fire escapes and no
 *    ladders: a balcony or a rung would promise a ledge / a climb that the
 *    server does not have.
 *  - MAST pieces are thin (≤ MAST_THICK) poles on a parapet's OUTER face; the
 *    only wide parts (aerial elements, beacons) sit ≥ MAST_CLEAR above the
 *    parapet top — far above any jump arc, so they never read as a step.
 *  - STREET pieces are decals on the ground slab (manholes, gully grates).
 * Everything is deterministic (position-hashed), so every client and every
 * round restart sees the same roofs.
 */
import type { MapBox, MapDef } from "@neon-purge/shared";

export const ROOF_LIFT = 0.05;
export const UNIT_PROUD = 0.08;
export const UNIT_LID = 0.07;
export const WALL_PROUD = 0.26;
export const MAST_THICK = 0.2;
export const MAST_CLEAR = 1.5;
export const PAD_CLEAR = 2.2;
export const CAP_LIFT = 0.05;
export const CAP_LIP = 0.035;

export type RoofMat = "metal" | "louvre" | "roof" | "paver" | "dark" | "glass" | "beacon" | "facade" | "tank";
export type RoofZone = "roof" | "unit" | "wall" | "cap" | "mast" | "street" | "skyline";

export interface RoofPieceX {
  mat: RoofMat;
  zone: RoofZone;
  /** box = axis-aligned box; cyl = cylinder whose axis is `axis` */
  shape: "box" | "cyl";
  axis: "x" | "y" | "z";
  /** centre + full AABB extents (for a cyl: its two cross extents = diameter) */
  x: number; y: number; z: number;
  sx: number; sy: number; sz: number;
  /** cyl only: (+axis end radius) / (-axis end radius); the wider end spans the
   *  full cross extent. 1 = straight cylinder (a dish is 4 or 0.25). */
  taper: number;
  color: number;
  /** collider index this piece is attached to (-1: ground) */
  host: number;
}

/** deterministic hash in [0,1) */
export function h01(a: number, b: number, salt: number): number {
  let x = Math.imul((Math.round(a * 10) * 73856093) ^ (Math.round(b * 10) * 19349663) ^ (salt * 83492791), 0x9e3779b1);
  x ^= x >>> 15; x = Math.imul(x, 0x85ebca6b); x ^= x >>> 13;
  return ((x >>> 0) % 100000) / 100000;
}

export interface AABB { x0: number; y0: number; z0: number; x1: number; y1: number; z1: number }
export const boxAABB = (b: MapBox): AABB => ({
  x0: b.p[0] - b.s[0] / 2, x1: b.p[0] + b.s[0] / 2,
  y0: b.p[1] - b.s[1] / 2, y1: b.p[1] + b.s[1] / 2,
  z0: b.p[2] - b.s[2] / 2, z1: b.p[2] + b.s[2] / 2,
});
export const pieceAABB = (p: RoofPieceX): AABB => ({
  x0: p.x - p.sx / 2, x1: p.x + p.sx / 2, y0: p.y - p.sy / 2, y1: p.y + p.sy / 2, z0: p.z - p.sz / 2, z1: p.z + p.sz / 2,
});
export const overlaps = (a: AABB, b: AABB, e = 0): boolean =>
  a.x0 < b.x1 - e && a.x1 > b.x0 + e && a.y0 < b.y1 - e && a.y1 > b.y0 + e && a.z0 < b.z1 - e && a.z1 > b.z0 + e;

/** One building as the shared generator lays it out: a solid wall block, a
 *  roof slab on it, a parapet ring on the slab and an AC box on the roof. */
export interface Building {
  wall: number; slab: number; ac: number;
  parapets: number[];
  x: number; z: number; w: number; d: number;
  wallTop: number; roofTop: number; parTop: number; parT: number;
  /** vertical scale of the generator (1, or 5 on the spiderweb skyscrapers) */
  v: number;
}

const near = (a: number, b: number, e = 0.02): boolean => Math.abs(a - b) <= e;

export function findBuildings(map: MapDef): Building[] {
  const bx = map.boxes, A = bx.map(boxAABB);
  const out: Building[] = [];
  for (let i = 0; i < bx.length; i++) {
    const b = bx[i];
    if (!near(A[i].y0, 0, 0.05) || b.s[1] < 3 || Math.min(b.s[0], b.s[2]) < 7) continue;
    const slab = bx.findIndex((s, j) => j !== i && near(s.p[0], b.p[0]) && near(s.p[2], b.p[2])
      && near(A[j].y0, A[i].y1) && s.s[1] <= 2 && near(s.s[0], b.s[0]) && near(s.s[2], b.s[2]));
    if (slab < 0) continue;
    const roofTop = A[slab].y1;
    const inFoot = (j: number): boolean => A[j].x0 >= A[i].x0 - 0.01 && A[j].x1 <= A[i].x1 + 0.01
      && A[j].z0 >= A[i].z0 - 0.01 && A[j].z1 <= A[i].z1 + 0.01;
    const parapets: number[] = [];
    let ac = -1;
    for (let j = 0; j < bx.length; j++) {
      if (j === i || j === slab || !inFoot(j)) continue;
      if (near(A[j].y0, roofTop) && Math.min(bx[j].s[0], bx[j].s[2]) <= 0.45) parapets.push(j);
      else if (A[j].y0 < roofTop + 0.01 && A[j].y0 >= A[i].y1 - 0.01 && near(bx[j].s[0], 2) && near(bx[j].s[2], 2)) ac = j;
    }
    if (parapets.length < 4) continue;
    out.push({
      wall: i, slab, ac, parapets,
      x: b.p[0], z: b.p[2], w: b.s[0], d: b.s[2],
      wallTop: A[i].y1, roofTop, parTop: A[parapets[0]].y1,
      parT: Math.min(bx[parapets[0]].s[0], bx[parapets[0]].s[2]),
      v: bx[slab].s[1] / 0.3,
    });
  }
  return out;
}

const C = {
  galv: 0xb9bdc0, galvDark: 0x8e9396, white: 0xd9dcdc, copper: 0x9a6a46, black: 0x26282a,
  membrane: [0x5d5a55, 0x6f6b64, 0x4e4c48, 0x7a766d], paver: 0xb8b2a6, frame: 0x9aa0a4,
  acBody: 0xc9ccc8, coping: 0x8f9496, screen: 0x6a717c, louvreTint: 0xd8dad6, beacon: 0xff2a1a, dish: 0xe4e4e0,
};

interface Face { host: number; axis: "x" | "z"; plane: number; sign: 1 | -1; t0: number; t1: number; y0: number; y1: number; tc: number }

function facesOf(A: AABB, host: number): Face[] {
  const tcz = (A.z0 + A.z1) / 2, tcx = (A.x0 + A.x1) / 2;
  return [
    { host, axis: "x", plane: A.x1, sign: 1, t0: A.z0, t1: A.z1, y0: A.y0, y1: A.y1, tc: tcz },
    { host, axis: "x", plane: A.x0, sign: -1, t0: A.z0, t1: A.z1, y0: A.y0, y1: A.y1, tc: tcz },
    { host, axis: "z", plane: A.z1, sign: 1, t0: A.x0, t1: A.x1, y0: A.y0, y1: A.y1, tc: tcx },
    { host, axis: "z", plane: A.z0, sign: -1, t0: A.x0, t1: A.x1, y0: A.y0, y1: A.y1, tc: tcx },
  ];
}

/** Everything, as data. Pure — no three.js objects are created here. */
export function rooftopsLayout(map: MapDef, lowSpec: boolean): RoofPieceX[] {
  const bx = map.boxes;
  const A = bx.map(boxAABB);
  const out: RoofPieceX[] = [];
  let ground = 0;
  for (let i = 0; i < bx.length; i++) if (bx[i].s[0] * bx[i].s[2] > bx[ground].s[0] * bx[ground].s[2]) ground = i;
  const gTop = A[ground].y1;
  const pads = map.jumpPads ?? [];
  const sky = map.id === "rooftop_spiderweb";

  /** free of every collider except the listed hosts (and the ground)? */
  const clear = (r: AABB, skip: number[]): boolean => {
    for (let i = 0; i < A.length; i++) {
      if (i === ground || skip.includes(i)) continue;
      if (overlaps(r, A[i], 0.004)) return false;
    }
    return true;
  };
  const padFree = (r: AABB, extra = 0): boolean => pads.every((p) =>
    Math.abs(p.p[1] - (r.y0 + r.y1) / 2) > 3
    || Math.max(r.x0 - p.p[0], p.p[0] - r.x1, 0) ** 2 + Math.max(r.z0 - p.p[2], p.p[2] - r.z1, 0) ** 2 > (PAD_CLEAR + extra) ** 2);

  const push = (p: Omit<RoofPieceX, "taper" | "axis" | "shape"> & Partial<Pick<RoofPieceX, "taper" | "axis" | "shape">>): void => {
    out.push({ shape: "box", axis: "y", taper: 1, ...p });
  };
  /** commit a group of pieces only if ALL pass `ok` — all-or-nothing props */
  const group = (ps: RoofPieceX[], skip: number[], needPad = true): boolean => {
    for (const p of ps) { const r = pieceAABB(p); if (!clear(r, skip) || (needPad && !padFree(r))) return false; }
    out.push(...ps);
    return true;
  };
  const mk = (p: Omit<RoofPieceX, "taper" | "axis" | "shape"> & Partial<Pick<RoofPieceX, "taper" | "axis" | "shape">>): RoofPieceX =>
    ({ shape: "box", axis: "y", taper: 1, ...p });

  /** a piece on a face: tangent centre t, vertical centre y, tangent width w,
   *  height hh, projection `proud`, embedded 1 cm into the host */
  const onFace = (f: Face, zone: RoofZone, mat: RoofMat, t: number, y: number, w: number, hh: number, proud: number,
    color: number, extra: Partial<RoofPieceX> = {}): RoofPieceX => {
    const nC = f.plane + f.sign * (proud / 2 - 0.005), nS = proud + 0.01;
    return mk({
      mat, zone, host: f.host, color,
      x: f.axis === "x" ? nC : t, z: f.axis === "z" ? nC : t, y,
      sx: f.axis === "x" ? nS : w, sz: f.axis === "z" ? nS : w, sy: hh, ...extra,
    });
  };

  const buildings = findBuildings(map);
  for (const B of buildings) {
    const { v } = B;
    const r = (k: number): number => h01(B.x, B.z, k + 31);
    const top = B.roofTop;
    // the deck inside the parapet ring (parapet thickness + a 0.15 m margin)
    const ix0 = B.x - B.w / 2 + B.parT + 0.15, ix1 = B.x + B.w / 2 - B.parT - 0.15;
    const iz0 = B.z - B.d / 2 + B.parT + 0.15, iz1 = B.z + B.d / 2 - B.parT - 0.15;
    const roofSkip = [B.slab, B.wall, ...B.parapets];
    /** a flat roof decal rect, kept only if clear (+ margin) of every box and pad */
    const roofRect = (mat: RoofMat, cx: number, cz: number, w: number, d: number, hh: number, color: number, y0 = top): RoofPieceX | null => {
      if (cx - w / 2 < ix0 || cx + w / 2 > ix1 || cz - d / 2 < iz0 || cz + d / 2 > iz1) return null;
      const p = mk({ mat, zone: "roof", host: B.slab, color, x: cx, y: y0 + hh / 2, z: cz, sx: w, sy: hh, sz: d });
      const m: AABB = { x0: cx - w / 2 - 0.25, x1: cx + w / 2 + 0.25, y0: top - 0.05, y1: top + 0.6, z0: cz - d / 2 - 0.25, z1: cz + d / 2 + 0.25 };
      if (!clear(m, roofSkip) || !padFree(pieceAABB(p))) return null;
      return p;
    };
    const iw = ix1 - ix0, id = iz1 - iz0;

    // ---- ROOF DECALS ------------------------------------------------------
    // membrane repair patches (two tones), 1-2 mm raised sheets
    const nPatch = lowSpec ? 2 : 4 + Math.floor(r(1) * 3);
    for (let k = 0; k < nPatch; k++) {
      const w = 0.9 + r(10 + k) * 2.4, d = 0.7 + r(20 + k) * 1.9;
      const cx = ix0 + w / 2 + r(30 + k) * Math.max(0, iw - w), cz = iz0 + d / 2 + r(40 + k) * Math.max(0, id - d);
      const p = roofRect("roof", cx, cz, w, d, 0.012 + k * 0.002, C.membrane[k % 4]);
      if (p) out.push(p);
    }
    // roof drains in two opposite corners: dark grate + galvanised ring
    for (const [sx, sz] of [[-1, -1], [1, 1]] as const) {
      const cx = sx < 0 ? ix0 + 0.5 : ix1 - 0.5, cz = sz < 0 ? iz0 + 0.5 : iz1 - 0.5;
      const ring = roofRect("metal", cx, cz, 0.46, 0.46, 0.022, C.galvDark);
      if (!ring) continue;
      out.push({ ...ring, shape: "cyl" });
      out.push(mk({ mat: "dark", zone: "roof", host: B.slab, color: C.black, shape: "cyl", x: cx, y: top + 0.028, z: cz, sx: 0.32, sy: 0.012, sz: 0.32 }));
    }
    // flush skylight on the bigger decks: frame + glass (+ mullion)
    if (iw * id >= 110 && !lowSpec) {
      const alongX = iw >= id;
      const sw = alongX ? 2.4 : 1.3, sd = alongX ? 1.3 : 2.4;
      const cx = B.x + (r(50) - 0.5) * Math.max(0, iw - sw - 3), cz = B.z + (r(51) - 0.5) * Math.max(0, id - sd - 3);
      const fr = roofRect("metal", cx, cz, sw, sd, 0.045, C.frame);
      if (fr) {
        out.push(fr);
        out.push(mk({ mat: "glass", zone: "roof", host: B.slab, color: 0xffffff, x: cx, y: top + 0.047, z: cz, sx: sw - 0.14, sy: 0.006, sz: sd - 0.14 }));
        out.push(mk({ mat: "metal", zone: "roof", host: B.slab, color: C.frame, x: cx, y: top + 0.044,
          z: cz, sx: alongX ? 0.05 : sw - 0.1, sy: 0.008, sz: alongX ? sd - 0.1 : 0.05 }));
      }
    }
    // flush roof hatch near the parapet (lid + dark gap)
    {
      const sxh = r(60) < 0.5 ? -1 : 1;
      const cx = sxh < 0 ? ix0 + 0.75 : ix1 - 0.75, cz = B.z + (r(61) - 0.5) * Math.max(0, id - 3);
      const kerb = roofRect("dark", cx, cz, 1.0, 1.0, 0.03, C.black);
      if (kerb) {
        out.push(kerb);
        out.push(mk({ mat: "metal", zone: "roof", host: B.slab, color: C.galv, x: cx, y: top + 0.037, z: cz, sx: 0.9, sy: 0.02, sz: 0.9 }));
      }
    }
    // concrete walkway pavers: a service path from the AC unit toward the deck
    // centre (where maintenance would walk) — 4 cm slabs with joints
    if (B.ac >= 0 && !lowSpec) {
      const a = A[B.ac];
      const acx = (a.x0 + a.x1) / 2, acz = (a.z0 + a.z1) / 2;
      const dx = B.x - acx, dz = B.z - acz;
      const alongX = Math.abs(dx) >= Math.abs(dz);
      const dir = alongX ? Math.sign(dx || 1) : Math.sign(dz || 1);
      for (let k = 0; k < 6; k++) {
        const off = 1.62 + k * 0.68;
        const cx = alongX ? acx + dir * off : acx, cz = alongX ? acz : acz + dir * off;
        const p = roofRect("paver", cx, cz, 0.6, 0.6, 0.035, C.paver);
        if (!p) break;
        out.push(p);
      }
    }

    // ---- THE AC UNIT: dress the collider box as a packaged rooftop unit ----
    if (B.ac >= 0 && !sky) {
      const a = A[B.ac];
      const yb = Math.max(a.y0, top);           // the visible base (it is sunk in the slab)
      const uh = a.y1 - yb;
      for (const f of facesOf(a, B.ac)) {
        const tw = f.t1 - f.t0, tc = (f.t0 + f.t1) / 2;
        // louvred side panel, framed by the body
        out.push(onFace(f, "unit", "louvre", tc, yb + 0.2 + (uh - 0.38) / 2, tw - 0.24, uh - 0.38, 0.025, C.louvreTint));
        // corner posts + base rail + top lip (the cabinet frame)
        for (const s of [-1, 1]) out.push(onFace(f, "unit", "metal", tc + s * (tw / 2 - 0.05), yb + uh / 2, 0.1, uh, 0.045, C.acBody));
        out.push(onFace(f, "unit", "metal", tc, yb + 0.08, tw, 0.16, 0.05, C.galvDark));
        out.push(onFace(f, "unit", "metal", tc, a.y1 - 0.06, tw, 0.12, 0.04, C.acBody));
      }
      // service panel + disconnect box and the refrigerant line set on the
      // face that looks toward the deck centre
      const fs = facesOf(a, B.ac);
      const fc = fs.reduce((best, f) => {
        const out1 = f.axis === "x" ? (B.x - f.plane) * f.sign : (B.z - f.plane) * f.sign;
        const outB = best.axis === "x" ? (B.x - best.plane) * best.sign : (B.z - best.plane) * best.sign;
        return out1 > outB ? f : best;
      });
      const tc = (fc.t0 + fc.t1) / 2;
      out.push(onFace(fc, "unit", "metal", tc - 0.35, yb + uh * 0.55, 0.32, 0.42, 0.075, C.galv));
      out.push(onFace(fc, "unit", "dark", tc + 0.32, yb + 0.12 + uh * 0.25, 0.05, uh * 0.5, 0.07, C.black, { shape: "cyl", axis: "y" }));
      out.push(onFace(fc, "unit", "metal", tc + 0.45, yb + 0.12 + uh * 0.25, 0.035, uh * 0.5, 0.06, C.copper, { shape: "cyl", axis: "y" }));
      // the top: fan shroud ring + dark grille (low — the top is standable)
      const cx = (a.x0 + a.x1) / 2, cz = (a.z0 + a.z1) / 2;
      out.push(mk({ mat: "metal", zone: "unit", host: B.ac, color: C.galvDark, shape: "cyl", x: cx, y: a.y1 + 0.03, z: cz, sx: 1.4, sy: 0.06, sz: 1.4 }));
      out.push(mk({ mat: "dark", zone: "unit", host: B.ac, color: C.black, shape: "cyl", x: cx, y: a.y1 + 0.032, z: cz, sx: 1.24, sy: 0.064, sz: 1.24 }));
      out.push(mk({ mat: "metal", zone: "unit", host: B.ac, color: C.galv, shape: "cyl", x: cx, y: a.y1 + 0.034, z: cz, sx: 0.22, sy: 0.068, sz: 0.22 }));
    }

    // ---- FACADES (normal-scale city only) ---------------------------------
    const wallA = A[B.wall];
    const wallFaces = facesOf(wallA, B.wall);
    // the mullion fins dressStructure already hangs on h ≥ 6 facades: keep out
    const finsAt = (len: number): number[] => {
      if (B.wallTop < 6) return [];
      const n = Math.min(4, Math.floor(len / 3.2) - 1);
      const t: number[] = [];
      for (let i = 1; i <= n; i++) t.push((i / (n + 1) - 0.5) * len);
      return t;
    };
    // the entrance face (door surround, awning, shop sign) is left alone
    const faceX = B.w >= B.d * 2.5 ? false : B.d >= B.w * 2.5 ? true : Math.abs(B.x) > Math.abs(B.z);
    const entSign = faceX ? -Math.sign(B.x || 1) : -Math.sign(B.z || 1);
    if (!sky) {
      let spouts = 0, units = 0;
      wallFaces.forEach((f, fi) => {
        if ((f.axis === "x") === faceX && f.sign === entSign) return;
        const len = f.t1 - f.t0;
        const fins = finsAt(len).map((t) => f.tc + t);
        const finFree = (t: number, w: number): boolean => fins.every((ft) => Math.abs(ft - t) > w / 2 + 0.27 + 0.12);
        // DOWNSPOUT near one end of the face: scupper through the parapet,
        // conductor head, pipe with brackets, shoe at the bottom
        if (spouts < (lowSpec ? 1 : 3) && r(70 + fi) < 0.75) {
          const end = r(80 + fi) < 0.5 ? -1 : 1;
          const t = f.tc + end * (len / 2 - 0.62);
          if (finFree(t, 0.34)) {
            const pipeTop = B.wallTop - 0.42, pipeBot = gTop + 0.25;
            const ps: RoofPieceX[] = [
              onFace(f, "wall", "metal", t, (pipeTop + pipeBot) / 2, 0.11, pipeTop - pipeBot, 0.13, C.galv, { shape: "cyl", axis: "y" }),
              onFace(f, "wall", "metal", t, B.wallTop - 0.24, 0.34, 0.36, 0.2, C.galvDark),
              onFace(f, "wall", "metal", t, gTop + 0.14, 0.16, 0.28, 0.2, C.galvDark),
            ];
            for (let y = pipeBot + 1.6; y < pipeTop - 0.6; y += 2.2) ps.push(onFace(f, "wall", "metal", t, y, 0.17, 0.05, 0.15, C.galvDark));
            // scupper on the parapet's outer face, right above the head
            const par = B.parapets.find((j) => {
              const pa = A[j];
              return f.axis === "x" ? near(f.sign > 0 ? pa.x1 : pa.x0, f.plane, 0.02) && t > pa.z0 + 0.2 && t < pa.z1 - 0.2
                : near(f.sign > 0 ? pa.z1 : pa.z0, f.plane, 0.02) && t > pa.x0 + 0.2 && t < pa.x1 - 0.2;
            });
            if (par !== undefined) {
              const pf = facesOf(A[par], par).find((q) => q.axis === f.axis && q.sign === f.sign)!;
              ps.push(onFace(pf, "wall", "metal", t, top + 0.09, 0.26, 0.14, 0.16, C.galvDark));
            }
            if (group(ps, [B.wall, B.slab, ...B.parapets], false)) spouts++;
          }
        }
        // WINDOW AC UNITS under facade windows (the facade texture's 1.125 m
        // window grid, box-local), upper storeys only — clear of the shop
        // signs, awnings and the jump arcs at the base
        if (B.wallTop >= 5) {
          const want = lowSpec ? 1 : 2 + Math.floor(r(90 + fi) * 2);
          for (let k = 0, tries = 0; k < want && tries < 8 && units < 9; tries++) {
            const col = Math.floor(r(100 + fi * 10 + tries) * Math.floor((len - 1.6) / 1.125));
            const tl = -len / 2 + 0.8 + col * 1.125;
            const tLocal = Math.round((tl - 0.5625) / 1.125) * 1.125 + 0.5625;
            const t = f.tc + tLocal;
            if (Math.abs(tLocal) > len / 2 - 0.9 || !finFree(t, 0.62)) continue;
            // window rows of the box-local facade grid, upper storeys only
            const yc = (wallA.y0 + wallA.y1) / 2;
            const m0 = Math.ceil((3.4 - yc - 0.5625) / 1.125), m1 = Math.floor((B.wallTop - 0.6 - yc - 0.5625) / 1.125);
            if (m1 < m0) break;
            const m = m0 + Math.floor(r(110 + fi * 10 + tries) * (m1 - m0 + 1));
            const y = yc + m * 1.125 + 0.5625 - 0.5;
            const ps = [
              onFace(f, "wall", "metal", t, y, 0.62, 0.38, 0.24, C.white),
              onFace(f, "wall", "louvre", t, y - 0.02, 0.5, 0.24, 0.255, C.louvreTint),
              onFace(f, "wall", "dark", t + 0.22, y - 0.28, 0.03, 0.18, 0.06, C.black, { shape: "cyl", axis: "y" }),
            ];
            if (group(ps, [B.wall], false)) { k++; units++; }
          }
        }
        // METER / DISTRIBUTION BOX at street level on one face
        if (fi === Math.floor(r(120) * 4)) {
          const t = f.tc + (r(121) - 0.5) * (len - 3);
          if (finFree(t, 0.8)) {
            group([
              onFace(f, "wall", "metal", t, gTop + 1.3, 0.62, 0.8, 0.16, C.galvDark),
              onFace(f, "wall", "dark", t + 0.18, gTop + 0.45, 0.06, 0.9, 0.08, C.black, { shape: "cyl", axis: "y" }),
              onFace(f, "wall", "metal", t - 0.2, gTop + 0.45, 0.07, 0.9, 0.09, 0xb08a3a, { shape: "cyl", axis: "y" }),
            ], [B.wall], false);
          }
        }
      });
    }

    // ---- PARAPET COPING: a pressed-metal cap on every parapet run --------
    // (the real detail that separates a built parapet from an extruded block;
    // ≤ CAP_LIFT on a top players can already stand on, ≤ CAP_LIP overhang).
    // Runs along z stop short of the full-width x-runs so no two caps share a
    // face (no corner z-fight).
    const bldSkip = [B.wall, B.slab, ...B.parapets];
    for (const j of B.parapets) {
      const pa = A[j];
      const thinX = pa.x1 - pa.x0 < 0.5;
      let x0 = pa.x0 - CAP_LIP, x1 = pa.x1 + CAP_LIP, z0 = pa.z0 - CAP_LIP, z1 = pa.z1 + CAP_LIP;
      if (thinX) {
        if (near(pa.z0, wallA.z0)) z0 = pa.z0 + B.parT + CAP_LIP + 0.002;
        if (near(pa.z1, wallA.z1)) z1 = pa.z1 - B.parT - CAP_LIP - 0.002;
        if (z1 - z0 < 0.3) continue;
      }
      const cap = mk({ mat: "metal", zone: "cap", host: j, color: C.coping,
        x: (x0 + x1) / 2, z: (z0 + z1) / 2, y: pa.y1 + (CAP_LIFT - 0.03) / 2, sx: x1 - x0, sz: z1 - z0, sy: CAP_LIFT + 0.03 });
      group([cap], bldSkip, false);
    }

    // ---- SKYSCRAPER ROOF SCREENS (spiderweb): the 5 m parapets are window
    // facade on both sides, so the roof read as a courtyard. Real towers hide
    // their roof plant behind a SCREEN WALL: clad the inner faces in framed
    // louvre panels with a top rail.
    if (sky) {
      for (const j of B.parapets) {
        const pa = A[j];
        for (const f of facesOf(pa, j)) {
          const thin = f.axis === "x" ? pa.x1 - pa.x0 < 0.5 : pa.z1 - pa.z0 < 0.5;
          if (!thin) continue;
          const centre = f.axis === "x" ? B.x : B.z;
          if ((centre - f.plane) * f.sign <= 0) continue;   // not the inward face
          // stop at the PERPENDICULAR parapets' inner faces, so the corner
          // panels are not swallowed by the run that crosses them
          const e0 = f.axis === "x" ? wallA.z0 : wallA.x0, e1 = f.axis === "x" ? wallA.z1 : wallA.x1;
          const t0 = f.t0 + (near(f.t0, e0) ? B.parT + 0.01 : 0), t1 = f.t1 - (near(f.t1, e1) ? B.parT + 0.01 : 0);
          const len = t1 - t0;
          if (len < 1) continue;
          const n = Math.max(1, Math.round(len / 2.6));
          const pw = len / n;
          const y0 = top + 0.06, y1 = pa.y1 - 0.25;
          for (let k = 0; k < n; k++) {
            const t = t0 + pw * (k + 0.5);
            group([
              onFace(f, "wall", "louvre", t, (y0 + y1) / 2, pw - 0.08, y1 - y0, 0.05, C.screen),
              onFace(f, "wall", "metal", t - pw / 2 + 0.04, (y0 + y1) / 2, 0.08, y1 - y0, 0.09, C.galvDark),
            ], [j, B.slab], false);
          }
          // top rail along the whole face
          group([onFace(f, "wall", "metal", (t0 + t1) / 2, y1 + 0.08, len, 0.16, 0.11, C.galvDark)], [j, B.slab], false);
        }
      }
    }

    // ---- PARAPET OUTER FACE: small dish ---------------------------------
    const parFaces = B.parapets.flatMap((j) => facesOf(A[j], j).filter((f) => {
      // the OUTER long face only
      const pa = A[j];
      if (f.axis === "x") return pa.x1 - pa.x0 < 0.5 && (f.sign > 0 ? near(pa.x1, wallA.x1) : near(pa.x0, wallA.x0));
      return pa.z1 - pa.z0 < 0.5 && (f.sign > 0 ? near(pa.z1, wallA.z1) : near(pa.z0, wallA.z0));
    }));
    if (!sky && parFaces.length && r(130) < 0.7) {
      const f = parFaces[Math.floor(r(131) * parFaces.length)];
      const t = (f.t0 + f.t1) / 2 + (r(132) - 0.5) * Math.max(0, f.t1 - f.t0 - 2);
      const y = (f.y0 + f.y1) / 2 + 0.05;
      group([
        onFace(f, "wall", "metal", t, y, 0.14, 0.18, 0.05, C.galvDark),
        // dish: a shallow cone facing outward (cross Ø 0.6, 0.12 deep)
        onFace(f, "wall", "metal", t, y, 0.6, 0.6, 0.17, C.dish, { shape: "cyl", axis: f.axis, taper: f.sign > 0 ? 4 : 0.25 }),
      ], [f.host, B.slab, B.wall, ...B.parapets], false);
    }

    // ---- MAST on an outer parapet corner ---------------------------------
    // a TV aerial on the normal city, an aviation mast with a red beacon on
    // the skyscrapers. Thin pole on the parapet's outer face; the wide parts
    // only well above the parapet top.
    if (parFaces.length && (sky || r(140) < 0.65)) {
      const f = parFaces[Math.floor(r(141) * parFaces.length)];
      const end = r(142) < 0.5 ? -1 : 1;
      const t = (end < 0 ? f.t0 : f.t1) - end * 0.35;
      const poleD = sky ? 0.12 : 0.05;
      const bot = sky ? B.parTop - 2.2 : (f.y0 + f.y1) / 2 - 0.25;
      const hgt = sky ? 7 + B.v * 0.4 + r(143) * 4 : 2.4 + r(143) * 1.2;
      const topY = B.parTop + hgt;
      const ps: RoofPieceX[] = [
        onFace(f, "mast", "metal", t, (bot + topY) / 2, poleD, topY - bot, poleD + 0.06, C.galv, { shape: "cyl", axis: "y" }),
        onFace(f, "mast", "metal", t, bot + 0.2, 0.14, 0.06, 0.12, C.galvDark),
        onFace(f, "mast", "metal", t, Math.min(B.parTop - 0.1, bot + 0.9), 0.14, 0.06, 0.12, C.galvDark),
      ];
      const poleC = f.plane + f.sign * ((poleD + 0.06) / 2 - 0.005);
      const at = (dt: number, y: number, sx: number, sy: number, sz: number, mat: RoofMat, color: number, shape: "box" | "cyl" = "box", axis: "x" | "y" | "z" = "y"): RoofPieceX =>
        mk({ mat, zone: "mast", host: f.host, color, shape, axis, x: f.axis === "x" ? poleC : t + dt, z: f.axis === "z" ? poleC : t + dt, y, sx, sy, sz });
      if (sky) {
        // guy collar + obstruction beacons (top + mid)
        ps.push(at(0, topY + 0.18, 0.32, 0.36, 0.32, "beacon", C.beacon, "cyl"));
        ps.push(at(0, B.parTop + hgt * 0.5, 0.26, 0.26, 0.26, "beacon", C.beacon, "cyl"));
        ps.push(at(0, B.parTop + hgt * 0.5 - 0.3, 0.28, 0.08, 0.28, "metal", C.galvDark, "cyl"));
      } else {
        // Yagi: a boom along the face tangent with 5 elements across it
        const boomY = topY - 0.25;
        const tang = f.axis === "x" ? "z" : "x";
        ps.push(at(0, boomY, f.axis === "z" ? 1.3 : 0.03, 0.03, f.axis === "x" ? 1.3 : 0.03, "metal", C.galvDark, "cyl", tang));
        for (let k = 0; k < 5; k++) {
          const dt = -0.55 + k * 0.275, el = 0.9 - k * 0.11;
          ps.push(at(dt, boomY, f.axis === "x" ? el : 0.018, 0.018, f.axis === "z" ? el : 0.018, "metal", C.galv, "cyl", f.axis));
        }
      }
      group(ps, [f.host, B.slab, ...B.parapets], false);
    }

  }

  // ---- STREET: manholes + gully grates on the ground slab ----------------
  {
    const half = Math.min(A[ground].x1, (map.bounds?.max?.[0] ?? A[ground].x1)) - 4;
    const n = lowSpec ? 6 : 14;
    let placed = 0;
    for (let k = 0; k < 80 && placed < n; k++) {
      const x = (h01(k, 3, 900) - 0.5) * 2 * half, z = (h01(k, 7, 901) - 0.5) * 2 * half;
      const round = k % 3 !== 0;
      const s = round ? 0.8 : 0.5;
      const m: AABB = { x0: x - s / 2 - 0.4, x1: x + s / 2 + 0.4, y0: gTop - 0.01, y1: gTop + 0.8, z0: z - s / 2 - 0.4, z1: z + s / 2 + 0.4 };
      if (!clear(m, []) || !padFree(m)) continue;
      if (round) {
        out.push(mk({ mat: "dark", zone: "street", host: ground, color: 0x2e2c2a, shape: "cyl", x, y: gTop + 0.008, z, sx: 0.86, sy: 0.016, sz: 0.86 }));
        out.push(mk({ mat: "metal", zone: "street", host: ground, color: 0x55524d, shape: "cyl", x, y: gTop + 0.012, z, sx: 0.74, sy: 0.014, sz: 0.74 }));
      } else {
        out.push(mk({ mat: "dark", zone: "street", host: ground, color: 0x1c1b1a, x, y: gTop + 0.009, z, sx: 0.5, sy: 0.018, sz: 0.9 }));
      }
      placed++;
    }
  }
  return out;
}
